/**
 * F2-c: sezione Utenti hotel (owner).
 *
 * Lista operatori (nome / username / ruolo / stato) con:
 *  - Aggiungi operatore (modal): email, nome, username, ruolo → il server
 *    genera password + PIN e li restituisce UNA volta (mostrati in un riquadro
 *    copiabile; avviso che non saranno più visibili).
 *  - Attiva/disattiva operatore + cambio ruolo (PATCH /owner/users/:id).
 *  - Reset PIN (POST /owner/users/:id/reset-pin) → nuovo PIN mostrato una volta.
 *
 * Mutations + invalidate della query detail. Nessun hash mai mostrato (il
 * server non lo restituisce).
 */
import { Check, Copy, KeyRound, Plus, Users } from '@hotel-manager/ui/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  type GeneratedCredentials,
  type HotelUser,
  createHotelUser,
  patchHotelUser,
  resetHotelUserPin,
} from '../../../api/owner.js';
import { ModalActions, ModalField, ModalShell, formatErr, modalInputCls } from './CamereSection.js';

interface Props {
  tenantId: string;
  users: HotelUser[];
}

type Role = 'reception' | 'housekeeping' | 'admin';
const ROLE_LABEL: Record<string, string> = {
  reception: 'Reception',
  housekeeping: 'Pulizie',
  admin: 'Admin',
  owner: 'Proprietario',
};

export function UtentiSection({ tenantId, users }: Props) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [pinFor, setPinFor] = useState<HotelUser | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['owner', 'hotel', tenantId] });

  const toggleActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => patchHotelUser(id, { active }),
    onSuccess: invalidate,
  });
  const changeRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: Role }) => patchHotelUser(id, { role }),
    onSuccess: invalidate,
  });

  const sorted = [...users].sort((a, b) => a.fullName.localeCompare(b.fullName));

  return (
    <section data-testid="utenti-section" className="flex flex-col gap-4">
      <header className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-bold text-text-primary flex items-center gap-1.5">
            <Users size={16} strokeWidth={1.75} className="text-accent-primary" />
            Operatori ({sorted.length})
          </h2>
          <p className="text-xs text-text-secondary mt-0.5">
            Gestisci gli account degli operatori e i loro PIN di accesso.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setAdding(true)}
          data-testid="user-add"
          className="inline-flex items-center gap-1.5 rounded-lg bg-accent-primary text-text-on-accent px-4 h-10 text-sm font-semibold hover:bg-accent-primary-hover transition-colors"
        >
          <Plus size={15} strokeWidth={2} /> Aggiungi operatore
        </button>
      </header>

      <div className="bg-bg-card border border-border-base rounded-2xl overflow-hidden shadow-hm-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-bg-base text-text-secondary text-[11px] uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Nome</th>
                <th className="text-left px-4 py-3 font-semibold">Username</th>
                <th className="text-left px-4 py-3 font-semibold">Ruolo</th>
                <th className="text-left px-4 py-3 font-semibold">Stato</th>
                <th className="px-4 py-3 font-semibold text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-text-muted">
                    Nessun operatore. Aggiungi il primo.
                  </td>
                </tr>
              )}
              {sorted.map((u) => {
                const isOwner = u.role === 'owner';
                return (
                  <tr
                    key={u.id}
                    className="border-t border-border-base"
                    data-testid={`row-user-${u.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-text-primary">{u.fullName}</div>
                      <div className="text-[11px] text-text-muted">{u.email}</div>
                    </td>
                    <td className="px-4 py-3 font-mono text-text-secondary">{u.username ?? '—'}</td>
                    <td className="px-4 py-3">
                      {isOwner ? (
                        <span className="text-text-secondary">{ROLE_LABEL.owner}</span>
                      ) : (
                        <select
                          value={u.role}
                          data-testid={`user-role-${u.id}`}
                          onChange={(e) =>
                            changeRole.mutate({ id: u.id, role: e.target.value as Role })
                          }
                          className="rounded-md border border-border-input bg-bg-card px-2 py-1 text-sm outline-none focus:border-accent-primary"
                        >
                          <option value="reception">Reception</option>
                          <option value="housekeeping">Pulizie</option>
                          <option value="admin">Admin</option>
                        </select>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                          u.active ? 'bg-[#eef5e9] text-[#5F8A50]' : 'bg-bg-muted text-text-muted'
                        }`}
                      >
                        {u.active ? 'Attivo' : 'Disattivo'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          type="button"
                          onClick={() => setPinFor(u)}
                          data-testid={`user-reset-pin-${u.id}`}
                          className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium text-text-secondary hover:bg-bg-muted"
                        >
                          <KeyRound size={13} /> Reset PIN
                        </button>
                        {!isOwner && (
                          <button
                            type="button"
                            onClick={() => toggleActive.mutate({ id: u.id, active: !u.active })}
                            data-testid={`user-toggle-${u.id}`}
                            className="rounded-md px-2 py-1.5 text-xs font-medium text-text-secondary hover:bg-bg-muted"
                          >
                            {u.active ? 'Disattiva' : 'Attiva'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {adding && (
        <AddUserModal tenantId={tenantId} onClose={() => setAdding(false)} onSaved={invalidate} />
      )}

      {pinFor && (
        <ResetPinModal user={pinFor} onClose={() => setPinFor(null)} onSaved={invalidate} />
      )}
    </section>
  );
}

function AddUserModal({
  tenantId,
  onClose,
  onSaved,
}: {
  tenantId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [username, setUsername] = useState('');
  const [role, setRole] = useState<Role>('reception');
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [creds, setCreds] = useState<GeneratedCredentials | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      createHotelUser(tenantId, {
        email,
        fullName,
        username: username.trim() || undefined,
        role,
      }),
    onSuccess: (res) => {
      setCreds(res.credentials);
      onSaved();
    },
    onError: (err: unknown) => setErrMsg(formatErr(err)),
  });

  // Step 2: credenziali generate — mostrate UNA volta.
  if (creds) {
    return (
      <ModalShell title="Operatore creato" onClose={onClose}>
        <p className="text-sm text-text-secondary">
          Comunica queste credenziali all'operatore. Non saranno più visibili dopo la chiusura.
        </p>
        <div className="mt-4 flex flex-col gap-2">
          <CredentialRow label="Password" value={creds.password} />
          <CredentialRow label="PIN" value={creds.pin} />
        </div>
        <div className="flex justify-end pt-5">
          <button
            type="button"
            onClick={onClose}
            data-testid="creds-done"
            className="rounded-lg bg-accent-primary text-text-on-accent px-4 h-10 text-sm font-semibold hover:bg-accent-primary-hover transition-colors"
          >
            Ho salvato le credenziali
          </button>
        </div>
      </ModalShell>
    );
  }

  return (
    <ModalShell title="Aggiungi operatore" onClose={onClose}>
      <form
        data-testid="user-modal"
        onSubmit={(e) => {
          e.preventDefault();
          setErrMsg(null);
          mutation.mutate();
        }}
        className="flex flex-col gap-4"
      >
        <ModalField label="Nome completo">
          <input
            data-testid="user-field-name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
            maxLength={120}
            className={modalInputCls}
          />
        </ModalField>
        <ModalField label="Email">
          <input
            data-testid="user-field-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            maxLength={160}
            className={modalInputCls}
          />
        </ModalField>
        <ModalField label="Username (per login PIN)">
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            maxLength={40}
            placeholder="opzionale"
            className={modalInputCls}
          />
        </ModalField>
        <ModalField label="Ruolo">
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            className={modalInputCls}
          >
            <option value="reception">Reception</option>
            <option value="housekeeping">Pulizie</option>
            <option value="admin">Admin</option>
          </select>
        </ModalField>
        <p className="text-[11px] text-text-muted">
          Password e PIN verranno generati automaticamente e mostrati una volta.
        </p>
        {errMsg && (
          <p role="alert" className="text-sm text-[#A8453A]">
            {errMsg}
          </p>
        )}
        <ModalActions
          onCancel={onClose}
          pending={mutation.isPending}
          confirmLabel="Crea operatore"
        />
      </form>
    </ModalShell>
  );
}

function ResetPinModal({
  user,
  onClose,
  onSaved,
}: {
  user: HotelUser;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [pin, setPin] = useState('');
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [newPin, setNewPin] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => resetHotelUserPin(user.id, pin.trim() || undefined),
    onSuccess: (res) => {
      setNewPin(res.pin);
      onSaved();
    },
    onError: (err: unknown) => setErrMsg(formatErr(err)),
  });

  if (newPin) {
    return (
      <ModalShell title="PIN aggiornato" onClose={onClose}>
        <p className="text-sm text-text-secondary">
          Nuovo PIN per <strong>{user.fullName}</strong>. Comunicalo all'operatore: non sarà più
          visibile.
        </p>
        <div className="mt-4">
          <CredentialRow label="PIN" value={newPin} />
        </div>
        <div className="flex justify-end pt-5">
          <button
            type="button"
            onClick={onClose}
            data-testid="pin-done"
            className="rounded-lg bg-accent-primary text-text-on-accent px-4 h-10 text-sm font-semibold hover:bg-accent-primary-hover transition-colors"
          >
            Fatto
          </button>
        </div>
      </ModalShell>
    );
  }

  return (
    <ModalShell title={`Reset PIN · ${user.fullName}`} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErrMsg(null);
          mutation.mutate();
        }}
        className="flex flex-col gap-4"
      >
        <ModalField label="Nuovo PIN (4 cifre, opzionale)">
          <input
            data-testid="pin-input"
            inputMode="numeric"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
            placeholder="lascia vuoto per generarlo"
            className={modalInputCls}
          />
        </ModalField>
        {errMsg && (
          <p role="alert" className="text-sm text-[#A8453A]">
            {errMsg}
          </p>
        )}
        <ModalActions
          onCancel={onClose}
          pending={mutation.isPending}
          confirmLabel="Reimposta PIN"
        />
      </form>
    </ModalShell>
  );
}

function CredentialRow({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard?.writeText(value).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      },
      () => undefined,
    );
  }
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-border-base bg-bg-base px-3 py-2.5">
      <div className="min-w-0">
        <div className="text-[11px] font-medium text-text-muted uppercase tracking-wide">
          {label}
        </div>
        <div className="font-mono text-base font-bold text-text-primary truncate">{value}</div>
      </div>
      <button
        type="button"
        onClick={copy}
        aria-label={`Copia ${label}`}
        className="inline-flex items-center gap-1 rounded-md border border-border-base bg-bg-card px-2.5 h-9 text-xs font-medium text-text-secondary hover:bg-bg-muted transition-colors shrink-0"
      >
        {copied ? <Check size={14} strokeWidth={2} /> : <Copy size={14} />}
        {copied ? 'Copiato' : 'Copia'}
      </button>
    </div>
  );
}
